import { computed, ref } from 'vue';
import { useRuntimeConfig, useCookie } from 'nuxt/app';
import { usePersistentState} from "~/composables/usePersistentState";
import { useHandleCode } from "~/composables/useHandleCode";
import {ApiResponse} from "~/types/response";
import { useI18n } from 'vue-i18n';

interface UserInfo {
	email: string;
	userName: string;
	contact: string;
	position: string;
	sex: number;
	birthDay?: string;
	memo?: string;
	countryCode: string;
	langCode: string;
	userRole: number;
	passwordUpdateDatetime: string | null;
	isPasswordExpired?: boolean;
	isTermsExpired?: boolean;
	data?: object;
}

// ✅ 인증 상태 관리 (전역 상태 + 쿠키)
export function useAuth() {
	const config = useRuntimeConfig();
	const { t, locale } = useI18n();
	const { handleErrorCode } = useHandleCode();

	// ✅ 전역 상태
	const accessToken = usePersistentState<string | null>('accessToken', () => null);
	const refreshToken = usePersistentState<string | null>('refreshToken', () => null);
	const userInfo = usePersistentState<UserInfo>('userInfo', () => ({
		email: '',
		userName: '',
		contact: '',
		position: '',
		sex: 0,
		countryCode: '',
		langCode: '',
		userRole: 0,
		data: {},
		passwordUpdateDatetime: null,
		isPasswordExpired: false,
		isTermsExpired: false
	}));
	const isAdmin = computed(() => userInfo.value.userRole === 90); // 90: 시스템 관리자

	// ✅ 쿠키 상태
	const accessTokenCookie = useCookie<string | null>('accessToken');
	const refreshTokenCookie = useCookie<string | null>('refreshToken');
	const userInfoCookie = useCookie<UserInfo>('userInfo');

	// ✅ 아이디 저장 관련 (쿠키 활용)
	const savedUserName = useCookie<string | null>('savedUserName', { default: () => null });
	const rememberMe = useCookie<boolean>('rememberMe', { default: () => false });

	// ✅ 입력 폼 상태
	const loginName = ref(savedUserName.value || '');
	const password = ref('');

	// 제어 버튼 상태값
	const isControlAllowed = computed(() => userInfo.value.userRole >= 90); // 시스템 관리자 권한이 있는 경우 제어 버튼 활성화;

	/**
	 * 유저 정보 가져오기
	 * @returns {UserInfo} 유저 정보
	 */
	const fetchUserInfo = async (userId: number): Promise<ApiResponse<UserInfo>> => {
		const response = await fetch(`${config.public.apiBaseUrl}/user/detail/${userId}`, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${accessToken.value}`,
				'Content-Type': 'application/json'
			}
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || 'API 요청 실패');
		}

		// ⚠️ response.ok 체크 생략 가능 (서버가 항상 ApiResponse 반환한다고 가정한다면)
		const data = await response.json();
		return data as ApiResponse<UserInfo>;
	};

	/**
	 * 로그인 처리
	 */
	const login = async (): Promise<void> => {
		const response = await fetch(`${config.public.authBaseUrl}/auth/login`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				loginName: loginName.value,
				password: password.value
			}),
		});

		if (!response.ok) {
			const errorData = await response.json();
			handleErrorCode(errorData.message || 'E99999'); // 로그인 실패 처리
			throw new Error(errorData.message || 'E99999');
		}

		const data = await response.json();

		if (data.result !== 'success') {
			handleErrorCode(data.message || 'E50001'); // 로그인 실패 처리
			throw new Error(data.message || 'E50001');
		}

		// ✅ rememberMe 처리
		savedUserName.value = rememberMe.value ? loginName.value : null;

		// ✅ 전역 상태 설정
		const { accessToken: at, refreshToken: rt } = data.data;
		accessToken.value = at;
		refreshToken.value = rt;

		// ✅ 쿠키 설정
		accessTokenCookie.value = at;
		refreshTokenCookie.value = rt;

		// ✅ 유저 정보 가져오기
		const userInfoResponse = await fetchUserInfo(data.data.userId);

		if(!userInfoResponse || userInfoResponse.result !== 'success') {
			handleErrorCode('E50000'); // 유저 정보 가져오기 실패
			throw new Error(userInfoResponse.message || 'E50001');
		}

		const userInfoData = userInfoResponse.data;

		userInfo.value = {
			userName: userInfoData.userName,
			userRole: userInfoData.userRole,
			email: userInfoData.email,
			contact: userInfoData.contact,
			position: userInfoData.position,
			sex: userInfoData.sex,
			birthDay: userInfoData.birthDay,
			memo: userInfoData.memo,
			countryCode: userInfoData.countryCode,
			langCode: userInfoData.langCode,
			data: userInfoData.data,
			passwordUpdateDatetime: userInfoData.passwordUpdateDatetime,
			isPasswordExpired: userInfoData.isPasswordExpired || false,
			isTermsExpired: userInfoData.isTermsExpired || false,
		};
		userInfoCookie.value = {
			email: userInfoData.email,
			userName: userInfoData.userName,
			contact: userInfoData.contact,
			position: userInfoData.position,
			sex: userInfoData.sex,
			countryCode: userInfoData.countryCode,
			langCode: userInfoData.langCode,
			userRole: userInfoData.userRole,
			data: userInfoData.data,
			passwordUpdateDatetime: userInfoData.passwordUpdateDatetime,
			isPasswordExpired: userInfoData.isPasswordExpired || false,
			isTermsExpired: userInfoData.isTermsExpired || false,
		};

		// 알림 권한 요청
		if ('Notification' in window && Notification.permission !== 'granted') {
			try {
				const permission = await Notification.requestPermission();
				if (permission === 'granted') {
					console.log('🔔 알림 권한 허용됨');
				} else {
					console.log('🔕 알림 권한 거부됨');
				}
			} catch (e) {
				console.warn('알림 권한 요청 중 오류:', e);
				handleErrorCode('E50001'); // 알림 권한 요청 실패
			}
		}
	};

	/**
	 * 아이디 중복 체크
	 * @param loginName	로그인 ID
	 */
	const fetchIdDuplicateCheck = async (loginName: string): Promise<ApiResponse<string>> => {
		// URL에 쿼리 파라미터로 loginName 추가
		const url = new URL(`${config.public.apiBaseUrl}/user/check/loginname?`);
		url.searchParams.append('loginName', loginName);

		const response = await fetch(url.toString(), {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || '아이디 중복 체크 실패');
		}

		const data = await response.json();
		return data as ApiResponse<string>;
	}

	/**
	 * 아이디 중복 체크 핸들러
	 * @param loginName	로그인 ID
	 */
	const handleIdDuplicateCheck = async (loginName: string): Promise<boolean> => {
		const response = await fetchIdDuplicateCheck(loginName);
		if (response.result === 'success') {
			return true; // 중복되지 않음(사용 가능)
		} else {
			handleErrorCode(response.message || '아이디 중복 체크 실패'); // 에러 코드 처리
			throw new Error(response.message || 'E50001');
		}
	}

	/**
	 * 이메일 중복 체크
	 * @param email 이메일 주소
	 */
	const fetchEmailDuplicateCheck = async (email: string): Promise<ApiResponse<string>> => {
		// URL에 쿼리 파라미터로 email 추가
		const url = new URL(`${config.public.apiBaseUrl}/user/check/email`);
		url.searchParams.append('email', email);

		const response = await fetch(url.toString(), {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || '이메일 중복 체크 실패');
		}

		const data = await response.json();
		return data as ApiResponse<string>;
	}

	/**
	 * 이메일 중복 체크 핸들러
	 * @param email 이메일 주소
	 */
	const handleEmailDuplicateCheck = async (email: string): Promise<boolean> => {
		const response = await fetchEmailDuplicateCheck(email);
		if (response.result === 'success') {
			return true; // 중복되지 않음(사용 가능)
		} else {
			handleErrorCode(response.message || '이메일 중복 체크 실패'); // 에러 코드 처리
			throw new Error(response.message || 'E50001');
		}
	}

	/**
	 * 핸드폰 번호 중복 체크
	 * @param phoneNumber	핸드폰 번호 (예: 01012345678)
	 */
	const fetchPhoneDuplicateCheck = async (phoneNumber: string): Promise<ApiResponse<string>> => {
		const url = new URL(`${config.public.apiBaseUrl}/user/check/phone`);
		url.searchParams.append('phone', phoneNumber);

		const response = await fetch(url.toString(), {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || '휴대폰 번호 중복 체크 실패');
		}

		const data = await response.json();
		return data as ApiResponse<string>;
	};

	/**
	 * 핸드폰 번호 중복 체크 핸들러
	 * @param phoneNumber	핸드폰 번호
	 */
	const handlePhoneDuplicateCheck = async (phoneNumber: string): Promise<boolean> => {
		const response = await fetchPhoneDuplicateCheck(phoneNumber);
		if (response.result === 'success') {
			return true; // 중복되지 않음 (사용 가능)
		} else {
			handleErrorCode(response.message || '휴대폰 번호 중복 체크 실패');
			throw new Error(response.message || 'E50001');
		}
	};

	// i18n 사용을 위해 Nuxt의 useI18n 훅을 가져옵니다.
	// 사용자 이름에 접미사를 추가하는 computed
	const userNameWithSuffix = computed(() => {
		const name = userInfo.value.userName || ''
		const lang = locale.value        // 이제 userInfo가 아닌 i18n locale 사용
		const sex  = userInfo.value.sex  // 0:남자, 1:여자

		let suffix: string
		if (lang === 'zh') {
			// 중국어는 성별에 따라 suffix 분기
			suffix = t(`common.userNameSuffix.zh.${sex === 1 ? 'female' : 'male'}`)
		} else {
			// ko, en 등은 공통 suffix
			suffix = t(`common.userNameSuffix.${lang}`)
		}

		return name + (suffix || '')
	})

	/**
	 * 로그아웃 처리
	 */
	const logout = async (): Promise<void> => {
		// ✅ 전역 상태 초기화 (null로 완전 초기화)
		accessToken.value = null;
		refreshToken.value = null;
		userInfo.value = {
			birthDay: "",
			contact: "",
			countryCode: "",
			email: "",
			langCode: "",
			memo: "",
			position: "",
			sex: 0,
			userName: '',
			userRole: 0,
			data: {},
			passwordUpdateDatetime: null,
			isPasswordExpired: false,
			isTermsExpired: false,
		};

		// ✅ 쿠키 초기화 (null로 삭제)
		accessTokenCookie.value = null;
		refreshTokenCookie.value = null;
		userInfoCookie.value = {
			contact: "",
			countryCode: "",
			email: "",
			langCode: "",
			position: "",
			sex: 0,
			userName: '',
			userRole: 0,
			data: {},
			passwordUpdateDatetime: null,
			isPasswordExpired: false,
			isTermsExpired: false,
		};

		// 세션 스토리지 초기화
		sessionStorage.clear();

		// 페이지 전체 새로고침
		window.location.replace('/')
	};

	return {
		// 상태
		accessToken,
		refreshToken,
		loginName,
		password,
		rememberMe,
		isAdmin,

		// 유저 정보
		userInfo,

		// 쿠키
		accessTokenCookie,
		refreshTokenCookie,

		// 제어 상태
		isControlAllowed,

		// 동작
		login,
		logout,

		// 아이디 중복 체크
		handleIdDuplicateCheck,
		// 이메일 중복 체크
		handleEmailDuplicateCheck,
		// 핸드폰번호 중복 체크
		handlePhoneDuplicateCheck,

		// getter
		isAuthenticated: computed(() => !!accessToken.value),
		getUserName: userNameWithSuffix,
		getUserRole: computed(() => userInfo.value.userRole),
		getSavedUserName: computed(() => savedUserName.value),
	};
}